/**
 * Dashboard Component - Main dashboard page for Ford PM Application
 * 
 * Features:
 * - Site/Location hierarchy navigation
 * - Real-time device monitoring with 40-second polling
 * - Device filtering by status and socket type
 * - KPI display (Total, Online, Offline devices)
 * - Device details drawer with logs and alerts
 * - Automatic data refresh with manual refresh option
 * 
 * @author CTS - Ford PM UI Team
 
 */

import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getSitesApi, getDevicesApi } from '../../api/dashboardApi';
import DashboardTable from './DashboardTable.jsx';
import PlugDrawer from './PlugDrawer.jsx';
import Kpi from '../../components/Kpi.jsx';

// Material UI imports
import {
  Typography,
  Select,
  Box,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
} from '@mui/icons-material';

// Custom styled components
import {
  DashboardContainer,
  DashboardOuterContainer,
  DashboardPageHeader,
  DashboardSidebarToggle,
  DashboardToggleRow,
  DashboardLayout,
  DashboardSidebar,
  DashboardMainContent,
  DashboardHierarchyPanel,
  DashboardTreeRoot,
  DashboardTreeList,
  DashboardTreeSite,
  DashboardTreeLocation,
  DashboardCountBadge,
  DashboardFiltersPanel,
  DashboardSearchContext,
  DashboardSearchContextText,
  DashboardFiltersRow,
  DashboardFiltersLeft,
  DashboardFilterLabel,
  DashboardSelect,
  DashboardMenuItem,
  DashboardPrimaryButton,

  DashboardKpisContainer,
  // DashboardLoadingOverlay,
  DashboardLoadingMessage,
  DashboardDeviceManagementInfo,
  DashboardErrorMessage,

  DashboardSpinner,
} from './Dashboard.styles';

import { AppLabels } from '../../constant/constant';
import DateUtils from '../../utils/DateUtils.jsx';

/**
 * Utility function to determine chip CSS class based on device status
 * Used for consistent status styling across the application
 * 
 * @param {string} status - Device status string
 * @returns {string} CSS class name for status chip
 */
function chipClass(status = '') {
  const s = status.toLowerCase();
  if (['ok', 'active', 'connected', 'online', 'ready'].some(k => s.includes(k))) return 'chip-ok';
  if (['warn', 'warning', 'degraded'].some(k => s.includes(k))) return 'chip-warn';
  if (['error', 'offline', 'failed', 'down'].some(k => s.includes(k))) return 'chip-error';
  return 'chip-muted';
}

/** ------------------------------------------------------------------
 * HELPER FUNCTIONS
 * ------------------------------------------------------------------*/

/**
 * Generates search context display text based on current selection
 * @param {string|null} selectedSite - Currently selected site ID
 * @param {string|null} selectedLocation - Currently selected location ID
 * @param {Array} apiSites - Array of site objects
 * @param {Array} apiLocations - Array of location objects
 * @param {Object} hierarchyCounts - Object containing device counts
 * @returns {string} Formatted context text
 */
const getSearchContextText = (selectedSite, selectedLocation, apiSites, apiLocations, hierarchyCounts) => {
  if (selectedSite === null && selectedLocation === null) {
    // Count only sites that have devices (siteCount > 0)
    const sitesHavingDevices = apiSites.filter(site => (hierarchyCounts.siteCounts[site.id] || 0) > 0);
    
    const sitesWithDevices = sitesHavingDevices.reduce((count, site) => {
      const siteNameCount=(site.name || '')
      .split('/')
      .map(name=>name.trim())
      .filter(Boolean)
      .length;
      return count + (siteNameCount|| 1);
    },0);
    
    return `Viewing all devices across ${sitesWithDevices} sites (${hierarchyCounts.total} total devices)`;
  }

  if (selectedSite && !selectedLocation) {
    const siteName = apiSites.find(s => s.id === selectedSite)?.name || 'Unknown Site';
    const deviceCount = hierarchyCounts.siteCounts[selectedSite] || 0;
    return `Viewing devices in: ${siteName} (${deviceCount} devices)`;
  }

  if (selectedLocation) {
    const siteName = apiSites.find(s => s.id === selectedSite)?.name || 'Unknown Site';
    const locationName = apiLocations.find(l => l.id === selectedLocation)?.name || 'Unknown Location';
    const deviceCount = hierarchyCounts.locationCounts[selectedLocation] || 0;
    return `Viewing devices in: ${siteName} → ${locationName} (${deviceCount} devices)`;
  }

  return 'Loading search context...';
};

/** ------------------------------------------------------------------
 * MAIN DASHBOARD
 * ------------------------------------------------------------------*/

export default function Dashboard() {
  // ========================================
  // ROUTE DETECTION
  // ========================================
  const location = useLocation();

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  // API Data State - Stores responses from backend APIs
  const [sitesData, setSitesData] = useState(null); // Sites hierarchy data from getSitesApi
  const [devicesData, setDevicesData] = useState([]); // Filtered devices data from getDevicesApi
  const [allDevicesData, setAllDevicesData] = useState([]); // All devices for hierarchy counts (unfiltered)

  // Loading States - Controls loading indicators
  const [loading, setLoading] = useState(true); // Initial page load
  const [devicesLoading, setDevicesLoading] = useState(false); // Hierarchy selection loading

  // Error States - Separate error handling for different API calls
  const [error, setError] = useState(''); // Sites API or general errors
  const [devicesError, setDevicesError] = useState(''); // Devices API specific errors

  // UI State - Controls user interface and filtering
  const [selectedSite, setSelectedSite] = useState(null);     // Selected site ID or null for all sites
  const [selectedLocation, setSelectedLocation] = useState(null); // Selected location ID or null
  const [expandedSites, setExpandedSites] = useState(new Set()); // Set of expanded site IDs in hierarchy
  const [statusFilter, setStatusFilter] = useState('all'); // Device status filter (all/online/offline)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Sidebar open/close state - default closed
  const view = 'table'; // Current view mode (table/map/reports)
  const [lastRefreshed, setLastRefreshed] = useState(new Date()); // Last data refresh timestamp

  // Pagination State - Controls table paging
  const [page, setPage] = useState(1); // Current page number
  const pageSize = AppLabels.dashboardLabels.pageSize; // Number of devices per page

  // Drawer State - Controls device details drawer
  const [openPlugId, setOpenPlugId] = useState(null); // Currently opened device ID
  const [selectedPlugDetails, setSelectedPlugDetails] = useState(null); // Device details for drawer


  // ========================================
  // REFS AND REAL-TIME POLLING
  // ========================================

  // Prevent duplicate API calls in React StrictMode (development)
  const hasFetchedData = useRef(false);

  // Real-time polling configuration
  const [isRealTimeEnabled, setIsRealTimeEnabled] = useState(true); // Toggle for real-time updates
  const intervalRef = useRef(null); // Reference to polling interval
  const isPlugDrawerOpen = Boolean(openPlugId); // Computed state for drawer visibility

  // ========================================
  // API FUNCTIONS
  // ========================================

  /**
   * Fetches sites hierarchy data from the backend
   * Called once on component mount to populate the site/location tree
   * @returns {Promise<boolean>} - Returns Promise that resolves to true if successful, false if failed
   */
  const fetchSitesData = async () => {
    try {
      setError(''); // Clear previous errors
      const response = await getSitesApi();
      setSitesData(response);
      return true; // Success
    } catch (err) {
      console.error(AppLabels.dashboardLabels.errorFetchingSites, err);
      setError(err.message);
      return false; // Failure
    }
  };

  /**
   * Fetches devices data with optional filtering parameters
   * 
   * @param {Object} params - API parameters (store_id, location_id)
   * @param {boolean} showLoading - Whether to show loading indicator
   */
  const fetchDevicesData = async (params = {}, showLoading = false) => {
    try {
      // Only clear data if not showing loading (to prevent flickering during auto-refresh)
      if (showLoading) {
        setDevicesData([]);
        setDevicesError(''); // Clear previous devices errors
      }
      if (showLoading) setDevicesLoading(true);

      const response = await getDevicesApi(params);
      console.log(AppLabels.dashboardLabels.devicesApiResponseLog, response);

      if (response?.devices) {
        setDevicesData(response.devices);
        setDevicesError(''); // Clear error on successful data load

        // Store all devices data for hierarchy counts (only on initial load without filters)
        if (Object.keys(params).length === 0) {
          setAllDevicesData(response.devices);
        }
      } else {
        console.warn(AppLabels.dashboardLabels.noDevicesDataWarning);
        setDevicesData([]);
        setDevicesError(''); // Clear error when no data but no actual error

        // Store empty array for hierarchy counts on initial load
        if (Object.keys(params).length === 0) {
          setAllDevicesData([]);
        }
      }
    } catch (err) {
      console.error(AppLabels.dashboardLabels.errorFetchingDevices, err);
      setDevicesData([]);
      // Check if error message contains "Server error" - show no devices instead of error
      if (err.message?.toLowerCase().includes('server error while')) {
        console.log('Server error detected, showing no devices found instead of error message');
        setDevicesError(''); // Clear error to show "no devices found" message in table
      } else {
        setDevicesError(err.message); // Set devices-specific error for other errors
      }
    } finally {
      if (showLoading) setDevicesLoading(false);
    }
  };

  /**
   * Initial data loading function - fetches both sites and devices
   * Called on component mount and when user clicks "Try Again" on errors
   */
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const sitesSuccess = await fetchSitesData(); // Load sites hierarchy first

      if (sitesSuccess) {
        await fetchDevicesData(); // Load all devices (no filters) only if sites loaded successfully

        // Start polling only if sites data was fetched successfully
        if (isRealTimeEnabled && !isPlugDrawerOpen && page === 1) {
          startRealTimePolling();
        }
      } else {
        // Stop polling if sites data failed to load
        stopRealTimePolling();
      }
    } catch (err) {
      // Errors are handled in individual fetch functions
      console.error(AppLabels.dashboardLabels.errorInFetchAllData, err);
      // Stop polling on any error
      stopRealTimePolling();
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================

  /**
   * Handles site/location selection from hierarchy tree
   * Fetches filtered devices based on selection and resets status filter
   * 
   * @param {string|null} siteId - Selected site ID or null for all sites
   * @param {string|null} locationId - Selected location ID or null
   */
  const handleHierarchySelection = async (siteId = null, locationId = null) => {
    setSelectedSite(siteId);
    setSelectedLocation(locationId);
    setStatusFilter('all'); // Reset status filter on hierarchy change

    // Build API parameters based on selection
    const params = {};
    if (siteId) params.store_id = siteId;
    if (locationId) params.location_id = locationId;

    // Fetch devices with filters and show loading indicator
    await fetchDevicesData(params, true);
  };

  /**
   * Toggles expansion/collapse of site in hierarchy tree
   * Prevents event bubbling to avoid triggering site selection
   * 
   * @param {string} siteId - Site ID to toggle
   * @param {Event} event - Click event
   */
  const toggleSiteExpansion = (siteId, event) => {
    event.stopPropagation(); // Prevent site selection when clicking expand button
    setExpandedSites(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(siteId)) {
        newExpanded.delete(siteId); // Collapse if expanded
      } else {
        console.log(AppLabels.dashboardLabels.expandingSiteLog, siteId);
        newExpanded.add(siteId); // Expand if collapsed
      }
      return newExpanded;
    });
  };

  /**
   * Manual refresh function - refreshes devices data with current filters
   * Updates last refreshed timestamp and maintains current hierarchy selection
   */
  const refresh = async () => {
    try {
      setLastRefreshed(new Date());
      // Refresh with current hierarchy selection
      const params = {};
      if (selectedSite) params.store_id = selectedSite;
      if (selectedLocation) params.location_id = selectedLocation;

      await fetchDevicesData(params, true);
    } catch (err) {
      console.error(AppLabels.dashboardLabels.errorDuringRefresh, err);
      // Error will be handled in fetchDevicesData
    }
  };

  // ========================================
  // REAL-TIME POLLING FUNCTIONS
  // ========================================

  /**
   * Starts real-time polling for device data updates
   * Polls every 40 seconds with current hierarchy filters
   * Automatically updates last refreshed timestamp
   */
  const startRealTimePolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current); // Clear existing interval
    }

    intervalRef.current = setInterval(async () => {
      try {
        // Build API parameters based on current selection
        const params = {};
        if (selectedSite) params.store_id = selectedSite;
        if (selectedLocation) params.location_id = selectedLocation;

        // Fetch devices data without showing loading indicator (silent refresh)
        await fetchDevicesData(params, false);
        setLastRefreshed(new Date());
      } catch (err) {
        console.error(AppLabels.dashboardLabels.errorDuringRealTimePolling, err);
      }
    }, AppLabels.dashboardTableLabels.autoRefreshValue * 1000); // 40 seconds interval
  }, [selectedSite, selectedLocation]);

  /**
   * Stops real-time polling and clears the interval
   */
  const stopRealTimePolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Toggles real-time polling on/off
   */
  const toggleRealTimePolling = useCallback(() => {
    setIsRealTimeEnabled(prev => !prev);
  }, []);

  /**
   * Opens device details drawer with selected device information
   * 
   * @param {string} plugId - Device ID to open in drawer
   */
  const handleOpenPlug = (plugId) => {
    const plugDetails = filtered.find(row => row.id === plugId);
    setOpenPlugId(plugId);
    setSelectedPlugDetails(plugDetails);
  };

  /**
   * Toggles sidebar open/close state
   */
  const toggleSidebar = () => {
    setIsSidebarOpen(prevOpen => !prevOpen);
  };

  // ========================================
  // EFFECTS
  // ========================================

  /**
   * Initial data loading effect - runs once on component mount
   * Prevents duplicate API calls in React StrictMode
   */
  useEffect(() => {
    if (!hasFetchedData.current) {
      hasFetchedData.current = true;
      fetchAllData();
    }
  }, []);

  /**
   * Real-time polling control effect
   * Starts polling when enabled, drawer is closed, user is on page 1, AND sites data is loaded
   * Pauses polling when drawer is open, user navigates away from page 1, or sites data is not available
   */
  useEffect(() => {
    // Check if user is still on dashboard page
    const isOnDashboard = location.pathname === '/dashboard' || location.pathname === '/';

    if (isRealTimeEnabled && !isPlugDrawerOpen && page === 1 && sitesData && isOnDashboard) {
      startRealTimePolling();
    } else {
      stopRealTimePolling();
    }

    // Cleanup on unmount
    return () => {
      stopRealTimePolling();
    };
  }, [isRealTimeEnabled, isPlugDrawerOpen, page, sitesData, location.pathname, startRealTimePolling, stopRealTimePolling]);

  /**
   * Restarts polling when hierarchy selection changes
   * Only restarts if polling is currently enabled, conditions are met, AND sites data is available
   */
  useEffect(() => {
    // Check if user is still on dashboard page
    const isOnDashboard = location.pathname === '/dashboard' || location.pathname === '/';

    if (isRealTimeEnabled && !isPlugDrawerOpen && page === 1 && sitesData && isOnDashboard) {
      stopRealTimePolling(); // Stop current polling
      startRealTimePolling(); // Start with new parameters
    }
  }, [selectedSite, selectedLocation, isRealTimeEnabled, isPlugDrawerOpen, page, sitesData, location.pathname, startRealTimePolling, stopRealTimePolling]);

  // ========================================
  // COMPUTED DATA AND MEMOIZED VALUES
  // ========================================

  /**
   * Transforms API sites data into normalized format for UI consumption
   * Adds default coordinates for future map functionality
   */
  const apiSites = useMemo(() => {
    if (!sitesData?.AllSites) return [];

    return sitesData.AllSites.map(site => ({
      id: site.store_id,
      name: site.store_name,
      centroid: { lat: 13.0827, lng: 80.2707 } // Default coordinates, can be enhanced later
    }));
  }, [sitesData]);

  /**
   * Flattens and transforms API locations data from all sites
   * Creates a flat array of locations with site associations
   */
  const apiLocations = useMemo(() => {
    if (!sitesData?.AllSites) return [];

    const locations = [];
    sitesData.AllSites.forEach(site => {
      site.locations.forEach(location => {
        locations.push({
          id: location.location_id,
          siteId: site.store_id,
          name: location.location_name,
        });
      });
    });
    return locations;
  }, [sitesData]);


  /**
   * Calculates device counts for hierarchy display
   * Uses allDevicesData to ensure consistent counts regardless of current filters
   * Validates location-to-site mappings to detect data inconsistencies
   */
  const hierarchyCounts = useMemo(() => {
    const dataToUse = allDevicesData.length ? allDevicesData : [];
    const siteCounts = {};
    const locationCounts = {};

    // Create a lookup for valid location-to-site mappings from sites data
    const locationToSiteMap = {};
    if (sitesData?.AllSites) {
      sitesData.AllSites.forEach(site => {
        site.locations.forEach(location => {
          locationToSiteMap[location.location_id] = site.store_id;
        });
      });
    }

    // Count devices by site and location
    dataToUse.forEach(device => {
      const siteId = device.store_id;
      const locationId = device.location_id;

      // Count devices by site
      if (siteId) {
        siteCounts[siteId] = (siteCounts[siteId] || 0) + 1;
      }

      // Count devices by location with data validation
      if (locationId) {
        const expectedSiteForLocation = locationToSiteMap[locationId];
        if (expectedSiteForLocation === siteId) {
          // Location belongs to the correct site
          locationCounts[locationId] = (locationCounts[locationId] || 0) + 1;
        } else {
          // Log data inconsistency for debugging
          console.warn(`Data inconsistency: Device ${device.store_plug_id} has store_id ${siteId} but location_id ${locationId} belongs to site ${expectedSiteForLocation}`);
        }
      }
    });

    return { siteCounts, locationCounts, total: dataToUse.length };
  }, [allDevicesData, sitesData]);

  /**
   * Calculates offline devices count by site for context display
   * Returns array of sites with their offline device counts
   */
  const offlineDevicesBySite = useMemo(() => {
    const dataToUse = allDevicesData.length ? allDevicesData : [];
    const siteOfflineCounts = {};
    const siteNames = {};

    // Build site name lookup
    if (sitesData?.AllSites) {
      sitesData.AllSites.forEach(site => {
        siteNames[site.store_id] = site.store_name;
      });
    }

    // Count offline devices by site
    dataToUse.forEach(device => {
      const siteId = device.store_id;
      const siteName = device.store_name || siteNames[siteId] || 'Unknown Site';
      const isOffline = device.status?.toLowerCase() === 'offline';

      if (siteId && isOffline) {
        if (!siteOfflineCounts[siteId]) {
          siteOfflineCounts[siteId] = {
            siteName: siteName,
            offlineCount: 0
          };
        }
        siteOfflineCounts[siteId].offlineCount += 1;
      }
    });

    // Convert to array and sort by offline count (descending)
    return Object.entries(siteOfflineCounts)
      .map(([siteId, data]) => ({
        siteId,
        siteName: data.siteName,
        offlineCount: data.offlineCount
      }))
      .filter(site => site.offlineCount > 0)
      .sort((a, b) => b.offlineCount - a.offlineCount);
  }, [allDevicesData, sitesData]);

  /**
   * Generates offline devices context text for display above filters
   */
  const getOfflineDevicesContextText = () => {
    if (!offlineDevicesBySite.length) {
      return 'All devices are online across all sites';
    }

    const sitesWithOfflineDevices = offlineDevicesBySite.length;

    if (sitesWithOfflineDevices === 1) {
      const site = offlineDevicesBySite[0];
      return `${site.siteName}: ${site.offlineCount} offline device${site.offlineCount > 1 ? 's' : ''}`;
    } else {
      // Show up to 3 sites with their counts
      const siteTexts = offlineDevicesBySite.map(site => 
        `${site.siteName} (${site.offlineCount})`
      );
      return `Offline devices across sites: ${siteTexts.join(', ')}`;
    }
    //  else {
    //   // Show total summary for many sites
    //   return `${totalOfflineDevices} offline devices across ${sitesWithOfflineDevices} sites`;
    // }
  };

  /**
   * Transforms and filters device data for table display
   * Applies client-side status and socket type filters
   * Site/location filtering is handled by API calls
   */
  // Helper function to transform device data to table row format
  const transformDeviceToRow = useCallback((device) => {
    // Extract status determination logic
    const deviceStatus = (() => {
      if (!device.status) return AppLabels.notApplicable;
      return device.status.toLowerCase() === 'online' ? 'online' : 'offline';
    })();

    const dualSupplyDevices =  device.dualcriticaldevices
    // Extract critical devices list
    const allCriticalDevices = [...(device.criticaldevices || []), ...(dualSupplyDevices || [])]
    const criticalDevicesList = allCriticalDevices.map(cd => cd.critical_id || cd.dual_critical_id).filter(id => id !== null && id !== undefined).join(', ') || AppLabels.notApplicable;
  
    return {
      id: device.store_plug_id ?? AppLabels.notApplicable,
      locationId: device.location_name,
      siteId: device.store_name,
      deviceMac: device.device_mac.toLowerCase() ?? AppLabels.notApplicable,
      model: device.model ?? AppLabels.notApplicable,
      deviceName: device.sp_DeviceName.toUpperCase() ?? AppLabels.notApplicable,
      criticalDeviceList: criticalDevicesList,
      criticalDevices: device.criticaldevices.filter(device => device.critical_id !== null) || [],
      dualSupplyDevices: dualSupplyDevices || [],
      status: deviceStatus,
      ip: device.ip ?? AppLabels.notApplicable,
      lastheartbeatat: DateUtils.formatDateWithLocalZone(device.last_heartbeat_at),
      smartPlugLocation: device.smart_plug_location ?? AppLabels.notApplicable,
      modemId: device.modemId ?? AppLabels.notApplicable,
    };
  }, []);

  const filtered = useMemo(() => {
    // Transform API device data to table row format
    let rows = [];
    if (devicesData?.length) {
      rows = devicesData.map(transformDeviceToRow);
    }

    // Apply client-side filters
    if (statusFilter !== 'all') {
      rows = rows.filter(r => chipClass(r.status) === statusFilter);
    }

    return rows;
  }, [devicesData, statusFilter, transformDeviceToRow]);


  /**
   * Calculates KPI statistics from filtered device data
   * Returns zero values if there's a devices error
   * Includes mock outage calculations for demonstration
   */
  const kpis = useMemo(() => {
    // Show zero values if there's a devices error
    if (devicesError) {
      return { total: 0, ok: 0, err: 0 };
    }

    // Calculate device status counts
    const total = devicesData.length;
    const ok = devicesData.filter(r => r.status.toLowerCase() === 'online').length;
    const err = devicesData.filter(r => r.status.toLowerCase() === 'offline').length;
    return { total, ok, err };
  }, [devicesError, devicesData]);

  // ========================================
  // PAGINATION AND UI EFFECTS
  // ========================================

  /**
   * Pagination calculations for device table
   */
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  /**
   * Reset to first page when filters change
   */
  useEffect(() => setPage(1), [statusFilter]);

  /**
   * Auto-expand sites in hierarchy when they are selected
   * Only runs when selectedSite changes, not when expandedSites changes
   */
  useEffect(() => {
    if (selectedSite && !expandedSites.has(selectedSite)) {
      setExpandedSites(prev => new Set([...prev, selectedSite]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]); // Removed expandedSites from dependencies to prevent re-expansion after manual collapse

  // ========================================
  // RENDER LOGIC
  // ========================================

  // Initial loading state
  if (loading) {
    return (
      <DashboardContainer>
        <DashboardOuterContainer>
          <DashboardPageHeader>
            <Typography variant="h2" component="h2">
              {AppLabels.dashboardLabels.pageTitle}
            </Typography>
            <Typography className="subtitle" component="p">
              {AppLabels.dashboardLabels.loadingDashboardData}
            </Typography>
          </DashboardPageHeader>
          <Box sx={{
            backgroundColor: 'white', 
            border: 'none',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <DashboardLoadingMessage>
              <DashboardSpinner sx={{ marginRight: '8px' }} />
              {AppLabels.dashboardLabels.loadingSitesAndDevices}
            </DashboardLoadingMessage>
          </Box>
        </DashboardOuterContainer>
      </DashboardContainer>
    );
  }

  // Error state (sites API or general errors)
  if (error) {
    return (
      <DashboardContainer>
        <DashboardOuterContainer>
          <DashboardPageHeader>
            <Typography variant="h2" component="h2">
              {AppLabels.dashboardLabels.pageTitle}
            </Typography>
            <Typography className="subtitle" component="p">
              {AppLabels.dashboardLabels.siteHierarchyDescription}
            </Typography>
          </DashboardPageHeader>
          <Box sx={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <Typography sx={{ fontSize: '24px', marginRight: '12px' }}>⚠️</Typography>
              <Typography sx={{
                color: '#ef4444',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}>
                {AppLabels.dashboardLabels.apiErrorTitle}
              </Typography>
            </Box>

            <DashboardErrorMessage component="div" sx={{
              color: '#ef4444',
              fontSize: '1rem',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              {error}
            </DashboardErrorMessage>

            <Box sx={{ textAlign: 'center' }}>
              <DashboardPrimaryButton onClick={fetchAllData}>
                {AppLabels.dashboardLabels.tryAgain}
              </DashboardPrimaryButton>
            </Box>
          </Box>
        </DashboardOuterContainer>
      </DashboardContainer>
    );
  }

  // Main dashboard render
  return (
    <DashboardContainer>
      <DashboardOuterContainer>
 <DashboardPageHeader>
          <Typography variant="h2" component="h2">
            {AppLabels.dashboardLabels.pageTitle}
          </Typography>
          <Typography className="subtitle" component="p">
            {AppLabels.dashboardLabels.siteHierarchyDescriptionAlt}
          </Typography>
        </DashboardPageHeader>
        
        <DashboardLayout>
          {/* LEFT SIDEBAR: Site → Location hierarchy */}
          <DashboardSidebar isOpen={isSidebarOpen}>
             {/* Page Header */}
            <DashboardHierarchyPanel>
              <Typography variant="h6" component="h3">
                {AppLabels.dashboardLabels.hierarchyTitle}
              </Typography>
              <DashboardTreeRoot
                className={!selectedSite && !selectedLocation ? 'active' : ''}
                onClick={() => handleHierarchySelection(null, null)}
              >
                {AppLabels.dashboardLabels.allSites}
                <DashboardCountBadge>({hierarchyCounts.total})</DashboardCountBadge>
              </DashboardTreeRoot>

              <Divider sx={{ borderColor: 'rgb(19, 34, 52)', marginTop: '4px', marginBottom: '4px' }} />

              <DashboardTreeList>
                {apiSites.map(site => {
                  const locs = apiLocations.filter(l => l.siteId === site.id);
                  const siteCount = hierarchyCounts.siteCounts[site.id] || 0;
                  const isExpanded = expandedSites.has(site.id);
                  const hasLocations = locs.length > 0;
                  return siteCount > 0 && (
                    <Box key={site.id}>
                      <DashboardTreeSite
                        className={selectedSite === site.id && !selectedLocation ? 'active' : ''}
                        onClick={() => handleHierarchySelection(site.id, null)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Box
                              onClick={hasLocations ? (e) => toggleSiteExpansion(site.id, e) : undefined}
                              sx={{
                                cursor: hasLocations ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                borderRadius: '4px',
                                '&:hover': hasLocations ? {
                                  backgroundColor: 'rgba(0, 0, 0, 0.1)'
                                } : {}
                              }}
                            >
                              {(() => {
                                if (!hasLocations) {
                                  return <ChevronRightIcon sx={{ fontSize: '16px', color: 'transparent' }} />;
                                }
                                if (isExpanded) {
                                  return <ExpandMoreIcon sx={{ fontSize: '16px', color: 'black' }} />;
                                }
                                return <ChevronRightIcon sx={{ fontSize: '16px', color: 'black' }} />;
                              })()}
                            </Box>
                            <span>{site.name}</span>
                          </Box>
                          <DashboardCountBadge>{siteCount}</DashboardCountBadge>
                        </Box>
                      </DashboardTreeSite>

                      {/* Locations - only show if site is expanded */}
                      {hasLocations && isExpanded && (
                        <Box sx={{ marginLeft: '16px' }}>
                          {locs.map(loc => {
                            const locCount = hierarchyCounts.locationCounts[loc.id] || 0;
                            return (
                              <DashboardTreeLocation
                                key={loc.id}
                                className={selectedLocation === loc.id ? 'active' : ''}
                                onClick={() => handleHierarchySelection(site.id, loc.id)}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                  <span>{loc.name}</span>
                                  <DashboardCountBadge>{locCount}</DashboardCountBadge>
                                </Box>
                              </DashboardTreeLocation>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </DashboardTreeList>
            </DashboardHierarchyPanel>
          </DashboardSidebar>

          {/* RIGHT PANEL: Main content area */}
          <DashboardMainContent isSidebarOpen={isSidebarOpen}>

             {/* Offline Devices Context Display */}
              {/* {offlineDevicesBySite.length > 0 && (
                <DashboardOfflineDeviceContext>
                  <DashboardSearchContextText 
                    variant="body2" 
                    sx={{ 
                      color: '#f44336',
                      fontWeight: 500
                    }}
                  >
                    {getOfflineDevicesContextText()}
                  </DashboardSearchContextText>
                </DashboardOfflineDeviceContext>
              )} */}
            <DashboardToggleRow>
              <DashboardSidebarToggle
                onClick={toggleSidebar}
                title={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
              >
                {isSidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
              </DashboardSidebarToggle>
              <DashboardSearchContext>
                <DashboardSearchContextText variant="body2">
                  {getSearchContextText(selectedSite, selectedLocation, apiSites, apiLocations, hierarchyCounts)}
                </DashboardSearchContextText>
              </DashboardSearchContext>
            </DashboardToggleRow>

            {/* Top Filters and Actions Bar */}
            <DashboardFiltersPanel>
              {/* Search Context Display */}
              {/* <DashboardSearchContext>
                <DashboardSearchContextText variant="body2">
                  {getSearchContextText(selectedSite, selectedLocation, apiSites, apiLocations, hierarchyCounts)}
                </DashboardSearchContextText>
              </DashboardSearchContext> */}

              <DashboardFiltersRow>
                <DashboardFiltersLeft>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
                    <DashboardFilterLabel variant="caption">
                      STATUS
                    </DashboardFilterLabel>
                    <DashboardSelect size="small">
                      <Select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        displayEmpty
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '40px',
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: '#fafafa',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              marginTop: '4px',
                            }
                          }
                        }}
                      >
                        <DashboardMenuItem value="all">{AppLabels.dashboardLabels.allStatusLabel}</DashboardMenuItem>
                        <DashboardMenuItem value="chip-ok">{AppLabels.dashboardLabels.onlineLabel}</DashboardMenuItem>
                        <DashboardMenuItem value="chip-error">{AppLabels.dashboardLabels.offlineLabel}</DashboardMenuItem>
                      </Select>
                    </DashboardSelect>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
                    <DashboardFilterLabel variant="caption">
                      Device count
                    </DashboardFilterLabel>
                    <DashboardKpisContainer className={devicesLoading ? 'loading-overlay' : ''}>
              <Kpi label={AppLabels.dashboardLabels.totalLabel} value={devicesLoading ? AppLabels.dashboardLabels.loadingEllipsis : kpis.total} isFromDashboard={true} borderColor="#ff0000"/>
              <Kpi label={AppLabels.dashboardLabels.onlineKpiLabel} value={devicesLoading ? AppLabels.dashboardLabels.loadingEllipsis : kpis.ok} isFromDashboard={true} borderColor="#22c55e"/>
              <Kpi label={AppLabels.dashboardLabels.offlineKpiLabel} value={devicesLoading ? AppLabels.dashboardLabels.loadingEllipsis : kpis.err} isFromDashboard={true} borderColor="#ef4444"/>
            </DashboardKpisContainer>
                  </Box>
                  
                </DashboardFiltersLeft>

                 {/* <DashboardFiltersRight>
                  <DashboardPrimaryButton
                    onClick={toggleRealTimePolling}
                    sx={{
                      backgroundColor: isRealTimeEnabled ? '#066fef' : '#233058',
                      color: isRealTimeEnabled ? '#ffffff' : '#9aa3c7',
                      '&:hover': {
                        backgroundColor: isRealTimeEnabled ? '#3d63cc' : '#2a3a6b',
                      },
                      minWidth: '180px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {isRealTimeEnabled ? AppLabels.dashboardLabels.realTimeOnText.replace('$', AppLabels.dashboardTableLabels.autoRefreshValue) : AppLabels.dashboardLabels.realTimeOffText}
                    {isPlugDrawerOpen && isRealTimeEnabled && (
                      <Typography component="span" sx={{
                        color: '#fbbf24',
                        fontSize: '0.75rem',
                        marginLeft: '8px'
                      }}>
                        {AppLabels.dashboardLabels.pausedText}
                      </Typography>
                    )}
                  </DashboardPrimaryButton>
                </DashboardFiltersRight>  */}
              </DashboardFiltersRow>
            </DashboardFiltersPanel>

            {/* KPI Statistics Display */}
            <DashboardKpisContainer className={devicesLoading ? 'loading-overlay' : ''}>
              <Kpi 
                label="TOTAL DEVICES" 
                value={devicesLoading ? AppLabels.dashboardLabels.loadingEllipsis : kpis.total} 
                description="All sites"
                borderColor="#3b82f6"
              />
              <Kpi 
                label="ONLINE" 
                value={devicesLoading ? AppLabels.dashboardLabels.loadingEllipsis : kpis.ok} 
                description={devicesLoading ? "" : `${kpis.total > 0 ? Math.round((kpis.ok / kpis.total) * 100) : 0}% healthy`}
                borderColor="#22c55e"
              />
              <Kpi 
                label="OFFLINE" 
                value={devicesLoading ? AppLabels.dashboardLabels.loadingEllipsis : kpis.err} 
                description="Needs attention"
                borderColor="#ef4444"
              />
            </DashboardKpisContainer>
            
            {/* Device Management Navigation Info */}
            <DashboardDeviceManagementInfo>
              {AppLabels.dashboardLabels.deviceManagementInfo}{" "}
              <Link to="/device-management">{AppLabels.dashboardLabels.deviceManagementLink}</Link> or{" "}
              <Link to="/device-management">{AppLabels.dashboardLabels.deviceManagementClickHere}</Link>.
            </DashboardDeviceManagementInfo>
            
            {/* Main Content Area - Table or Loading */}
            {devicesLoading ? (
              <Box sx={{
                backgroundColor: 'lightgray',
                border: 'none',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
                background: 'white'
              }}>
                <DashboardLoadingMessage>
                  <DashboardSpinner sx={{ marginRight: '8px' }} />
                  {AppLabels.dashboardLabels.loadingDevicesOnly}
                </DashboardLoadingMessage>
              </Box>
            ) : view === 'table' && (
              <DashboardTable
                rows={pageRows}
                fullRows={filtered}
                page={page}
                totalPages={totalPages}
                onPage={setPage}
                onOpenPlug={handleOpenPlug}
                error={devicesError}
                lastRefreshed={lastRefreshed}
                onRefresh={refresh}
                isRealTimeEnabled={isRealTimeEnabled}
                isRealTimePaused={isPlugDrawerOpen}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
              />
            )}
          </DashboardMainContent>
        </DashboardLayout>

        {/* Device Details Drawer */}
        {openPlugId && (
          <PlugDrawer
            plugId={openPlugId}
            plugDetails={selectedPlugDetails}
            onClose={() => {
              setOpenPlugId(null);
              setSelectedPlugDetails(null);
            }}
          />
        )}
      </DashboardOuterContainer>
    </DashboardContainer>
  );
}

